import React, { useState } from 'react';
import { forEach, get } from 'lodash';

import { CardFacade } from 'utils/facades/card-facade';
import { StoryFacade } from 'utils/facades/story-facade';
import { PersonalityTestFacade } from 'utils/facades/personality-test-card-facade';

import { postGallery } from 'admin/actions/story/gallery';
import { useAdminSelector, useAdminDispatch } from 'admin/reducers';
import { selectEditableCard, selectEditableStorySettings } from 'admin/reducers/card-editor/selectors';
import { selectEditableStory } from 'admin/reducers/story-editor/selectors';

import { MODAL } from 'admin/constants/common';
import { characterPoints, CountType } from 'common/constants';

import Text from 'admin/components/common/Text';
import Button from 'admin/components/common/Button';
import { Modal } from 'admin/components/common/Modal';
import { Column } from 'admin/components/common/Grid';
import SettingsModalLayout from 'admin/components/common/SettingsModalLayout';
import type { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';

import type { InspectorTypes } from 'admin/components/pages/Story/CardEditor/Inspector/types';
import { UPDATE_TARGET } from 'admin/components/pages/Story/CardEditor/Inspector/constants';

import Form, { Props as FormProps, Values } from './Form';
import GeneralView from './GeneralView';
import ShareView from './ShareView';
import SeoView from './SeoView';

import { names, parseScore, score, text, VIEW } from './utils';

import css from './CardSettingsModal.scss';

export type CardSettingsModalData = { onUpdate: InspectorTypes.OnUpdate; defaultView?: VIEW };

type ModalProps = ModalManagerProvidedProps<MODAL.CARD_SETTINGS>;

const viewMap = {
	[VIEW.GENERAL]: GeneralView,
	[VIEW.SHARE]: ShareView,
	[VIEW.SEO]: SeoView,
};

const CardSettings: React.FC<Omit<ModalProps, 'open'>> = props => {
	const editableCard = useAdminSelector(selectEditableCard)!;
	const editableStory = useAdminSelector(selectEditableStory)!;
	const { cards } = useAdminSelector(selectEditableStorySettings);
	const dispatch = useAdminDispatch();

	const { data } = props;
	const cardFacade = new CardFacade(editableCard);
	const storyFacade = new StoryFacade(editableStory);
	const hasAnswerScore = cardFacade.hasFeature.answerScore;

	const initialValues: Values = {
		_id: cardFacade.cardId,
		name: cardFacade.data.name,
		settings: { cards },
		_setScoreForAll: false,
	};

	const [view, setView] = useState<VIEW>(props.data.defaultView || VIEW.GENERAL);
	const [error, setError] = useState(false);

	const cardIds = React.useMemo(() => Object.keys(cards ?? {}), [cards]);

	const menu = React.useMemo(() => {
		return [
			{
				label: 'General',
				isActive: VIEW.GENERAL === view,
				disabled: error,
				onClick: error ? undefined : () => setView(VIEW.GENERAL),
			},
			{
				label: 'Share',
				isActive: VIEW.SHARE === view,
				disabled: error,
				onClick: error ? undefined : () => setView(VIEW.SHARE),
			},
			{
				label: 'SEO',
				isActive: VIEW.SEO === view,
				disabled: error,
				onClick: error ? undefined : () => setView(VIEW.SEO),
			},
		];
	}, [error, view]);

	const onSubmit = async ({ _id, ...values }: Values) => {
		if (_id !== cardFacade.cardId) {
			// todo: check if this is needed. currently this feature is not used, "_id" ignored
			// It's a critical change that requires a page reload.
			// I think it is a separate update that should be performed independently of other changes.
			// await dispatch(updateLatestStory({ ...storyFacade.requestData, getStoryOnComplete: false }));
		}

		const fieldNames = Object.keys(names) as Array<keyof typeof names>;
		const payload = fieldNames.reduce(
			(acc, key) => {
				const name = names[key];
				const pathToValue = typeof name === 'string' ? name : name(cardFacade.cardId);
				let value = get(values, pathToValue);

				if (key === score) {
					value = parseScore(value);
				}

				acc.name.push(pathToValue);
				acc.value.push(value);
				return acc;
			},
			{
				name: [] as string[],
				value: [] as (string | number | undefined)[],
				updateTarget: UPDATE_TARGET.STORY_SETTINGS,
			}
		);

		const answers = hasAnswerScore && values.settings.cards?.[cardFacade.cardId]?.answers;
		if (answers && cardFacade.card instanceof PersonalityTestFacade) {
			// use facade, but not value for get countType to support old stories
			const ptCountType = cardFacade.card.getCountType(values.settings);
			const answersPath = `settings.cards.${cardFacade.cardId}.answers`;

			forEach(answers, (answerSettings, answerId) => {
				if (ptCountType === CountType.score && characterPoints in answerSettings) {
					payload.name.push(`${answersPath}.${answerId}.${characterPoints}`);
					payload.value.push(''); // delete
				} else if (ptCountType === CountType.character && score in answerSettings) {
					payload.name.push(`${answersPath}.${answerId}.${score}`);
					payload.value.push(''); // delete
				}
			});
		}

		// add to payload updated score for all cards
		if (values._setScoreForAll) {
			const scoreValue = parseScore(get(values, names[score](cardFacade.cardId)));
			storyFacade.cards.forEach(card => {
				const _cardFacade = new CardFacade(card);
				if (_cardFacade.hasFeature.score && _cardFacade.cardId !== cardFacade.cardId) {
					payload.name.push(names[score](_cardFacade.cardId));
					payload.value.push(scoreValue);
				}
			});
		}

		await data.onUpdate(payload);

		props.close();
	};

	const renderFormContent = (formProps: FormProps) => {
		const Content = viewMap[view];

		return (
			<>
				<Content
					card={cardFacade}
					postGallery={params => dispatch(postGallery(params))}
					storyId={storyFacade.storyId}
					formChange={formProps.change}
					cardIds={cardIds}
				/>

				<div className={css.footer}>
					<Column justifyContent="flex-start">
						<Button
							type="submit"
							view="primary"
							textSize="label"
							className={css.button}
							disabled={formProps.pristine || formProps.invalid}
						>
							{text('save')}
						</Button>
						<Button onClick={props.close} textSize="label" view="secondary-gray" className={css.button}>
							{text('discard')}
						</Button>
					</Column>
				</div>
			</>
		);
	};

	const renderContent = () => {
		return (
			<Form
				onSubmit={onSubmit}
				setError={setError}
				cardId={cardFacade.cardId}
				cardType={cardFacade.type}
				initialValues={initialValues}
			>
				{renderFormContent}
			</Form>
		);
	};

	const renderHeader = () => {
		return (
			<>
				<Text size="subtitle" weight="semibold" compact text={text('title')} />
				<Text
					size="footnote"
					compact
					text={CardFacade.getCardNameByType(editableCard.type)}
					className={css.cardType}
				/>
			</>
		);
	};

	return <SettingsModalLayout menu={menu} renderHeader={renderHeader} renderContent={renderContent} />;
};

const CardSettingsModal = ({ open, ...props }: ModalProps) => {
	return (
		<Modal
			maskColor="black"
			theme="dark"
			open={open}
			destroyOnClose
			width={parseInt(css.modalWidth, 10)}
			className={css.modal}
			transitionName=""
		>
			{/* modal will unmount its content on close, but won't destroy itself */}
			<CardSettings {...props} />
		</Modal>
	);
};

export default CardSettingsModal;
